import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Skeleton } from '@vnetwork/ui/primitives';
import { useMyServices } from '@/api/services';
import { type StreamingProviderName, STREAMING_PRODUCTS } from './streaming-activate';
import { StreamingActivateLanding } from './StreamingActivateLanding';

interface StreamingLandingGateProps {
  /** Animal codename of the provider this gate guards ('penguin' or 'lens'). */
  provider: StreamingProviderName;
}

/**
 * Landing gate for a streaming module at its base route (no serviceId).
 * Mirrors WaapLandingGate:
 *   - loading         → skeleton
 *   - service exists  → redirect into its console
 *   - none            → StreamingActivateLanding activation hero
 *
 * Used as the index route inside LivePage (/services/live) and
 * VodPage (/services/vod).
 */
export function StreamingLandingGate({ provider }: StreamingLandingGateProps) {
  const { data: services = [], isLoading } = useMyServices();

  const product = STREAMING_PRODUCTS[provider];

  const existing = useMemo(
    () => services.find((s) => s.provider?.name?.trim().toLowerCase() === provider),
    [services, provider],
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 200 }} />
      </Container>
    );
  }

  if (existing) {
    return <Navigate to={product.consoleRoute(existing.id)} replace />;
  }

  return <StreamingActivateLanding provider={provider} />;
}

export default StreamingLandingGate;
